<script setup lang="ts">
import PdsAvatar from '@/components/pds/PdsAvatar.vue';
import PdsThemeSwitch from '@/components/pds/PdsThemeSwitch.vue';
import { computed, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useOperator } from '../composables/useOperator';
import { usePdsHistoryStore } from '../stores/history.store';
import { usePdsStockStore } from '../stores/stock.store';
import DisplayPrefsModal from './DisplayPrefsModal.vue';

const emit = defineEmits<{ close: [] }>();

const operator = useOperator();
const stock = usePdsStockStore();
const history = usePdsHistoryStore();
const panel = ref<HTMLElement>();

const entries = computed(() =>
  [
    {
      label: 'Ce qu’il faut préparer',
      meta: `${stock.todayTodoCount} références`,
      to: '/sortir',
    },
    {
      label: 'Commandes remboursées et refusées',
      meta: '',
      to: { path: '/journal', query: { statut: 'cancelled' } },
    },
    {
      label: 'Historique des commandes',
      meta:
        history.overallTotal === null
          ? '—'
          : `${history.overallTotal} commande${history.overallTotal > 1 ? 's' : ''}`,
      to: '/journal',
      // Masquée pour l'instant : la page /journal reste en place.
      hidden: true,
    },
  ].filter((entry) => !entry.hidden),
);
const prefsOpen = ref(false);

const entryClass =
  'bg-carte flex h-[58px] w-full items-center justify-between gap-3 rounded-[12px] px-5 text-left text-[16px] leading-[1.4] font-semibold active:bg-filet-doux';

onMounted(() => {
  panel.value?.focus();
  void history.refreshOverallTotal();
});
</script>

<template>
  <Teleport v-if="!prefsOpen" to="body">
    <div
      class="font-pds bg-voile fixed inset-0 z-50"
      @mousedown.self="emit('close')"
      @keydown.esc="emit('close')"
    >
      <div
        ref="panel"
        role="dialog"
        aria-label="Menu"
        tabindex="-1"
        class="bg-creux text-encre absolute top-[10px] right-3 flex w-[400px] max-w-[calc(100%-24px)] flex-col gap-[6px] rounded-[24px] p-4 outline-none sm:right-5 lg:top-[82px]"
      >
        <div class="xl:hidden"><PdsThemeSwitch /></div>

        <div class="flex items-center gap-3 pb-2 pl-2">
          <PdsAvatar :initials="operator.initials.value" />
          <div class="min-w-0 flex-1">
            <p class="text-[14px] leading-[1.4] font-semibold">
              {{ operator.name.value }}
            </p>
            <p class="text-encre-douce truncate text-[12px] leading-[1.45]">
              {{
                [operator.role.value, operator.store.value]
                  .filter(Boolean)
                  .join(' · ')
              }}
            </p>
          </div>
          <RouterLink
            to="/operateur"
            class="active:bg-second-appui flex h-12 items-center rounded-[12px] px-[13px] text-[14px] leading-[1.4] font-semibold"
          >
            Changer
          </RouterLink>
        </div>

        <ul class="flex flex-col gap-[6px]">
          <li v-for="entry in entries" :key="entry.label">
            <RouterLink :to="entry.to" :class="entryClass">
              {{ entry.label }}
              <span
                v-if="entry.meta"
                class="text-encre-douce text-[12px] font-normal"
              >
                {{ entry.meta }}
              </span>
            </RouterLink>
          </li>
        </ul>
        <ul class="mt-[10px] flex flex-col gap-[6px]">
          <li>
            <button type="button" :class="entryClass" @click="prefsOpen = true">
              Affichage des commandes
            </button>
          </li>
          <li class="max-lg:hidden">
            <RouterLink to="/reglages" :class="entryClass">
              Réglages et périphériques
            </RouterLink>
          </li>
        </ul>
      </div>
    </div>
  </Teleport>
  <DisplayPrefsModal
    v-if="prefsOpen"
    @close="
      prefsOpen = false;
      emit('close');
    "
  />
</template>
